import { useState } from "react";
import { api } from "../api/client.js";
import { getAdminToken } from "./auth.js";

export const formatAmount = (amount, currency) => {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  });
  const digits = formatter.resolvedOptions().maximumFractionDigits;
  return formatter.format(amount / 10 ** digits);
};

export const formatDate = (value) =>
  new Intl.DateTimeFormat("en", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));

export const formatPaymentStatus = (status) =>
  status ? status.replace(/_/g, " ") : "Unknown";

export const getUpsTrackingUrl = (trackingNumber) =>
  `https://www.ups.com/track?tracknum=${encodeURIComponent(trackingNumber)}`;

export const formatBillingAddress = (address) => {
  if (!address) return [];

  return [
    address.line1,
    address.line2,
    [address.city, address.state].filter(Boolean).join(", "),
    [address.zip, address.country].filter(Boolean).join(" "),
  ].filter(Boolean);
};

export const BillingAddressCell = ({ address }) => {
  const lines = formatBillingAddress(address);

  if (lines.length === 0) return <small>Not provided</small>;

  return lines.map((line, index) => (
    <small key={`${line}-${index}`}>{line}</small>
  ));
};

export const ShipmentCell = ({
  order,
  actions,
  workingId,
  onRetry,
  onDownload,
  onDroppedOff,
  onVoid,
}) => {
  const shipment = order.shipment || {};
  const shipping = order.shipping || {};
  const status = order.shipmentStatus || "not_created";
  const labels = shipment.labels || [];

  return (
    <div className="admin-shipment-cell">
      <span className={`admin-payment-status is-${status}`}>
        {formatPaymentStatus(status)}
      </span>
      {(shipping.serviceName || shipping.serviceCode) && (
        <small>{shipping.serviceName || shipping.serviceCode}</small>
      )}
      {order.shippingAmount > 0 && (
        <small>
          Shipping: {formatAmount(order.shippingAmount, order.currency || "USD")}
        </small>
      )}
      {shipping.estimatedDelivery && (
        <small>UPS estimate: {shipping.estimatedDelivery}</small>
      )}
      {shipment.trackingNumbers?.map((trackingNumber) => (
        <small key={trackingNumber}>
          <a
            href={getUpsTrackingUrl(trackingNumber)}
            target="_blank"
            rel="noreferrer"
          >
            {trackingNumber}
          </a>
        </small>
      ))}
      {order.shipmentError && <small>{order.shipmentError}</small>}
      <div className="admin-shipment-actions">
        {labels.map((label) => (
          <button
            type="button"
            key={`${order.id}-${label.index}`}
            onClick={() => onDownload(order, label.index)}
            disabled={workingId === order.id}
          >
            Download label {labels.length > 1 ? label.index + 1 : ""}
          </button>
        ))}
        {["not_created", "label_failed"].includes(status) &&
          order.paymentStatus === "succeeded" && (
            <button
              type="button"
              onClick={() => onRetry(order)}
              disabled={workingId === order.id}
            >
              {actions.retry}
            </button>
          )}
        {status === "label_created" && (
          <>
            <button
              type="button"
              onClick={() => onDroppedOff(order)}
              disabled={workingId === order.id}
            >
              Mark dropped off
            </button>
            <button
              type="button"
              onClick={() => onVoid(order)}
              disabled={workingId === order.id}
            >
              Void label
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default function OrderManager({ orders, onChanged, onUnauthorized }) {
  const [deletingId, setDeletingId] = useState("");
  const [workingId, setWorkingId] = useState("");
  const [error, setError] = useState("");

  const removeOrder = async (order) => {
    if (deletingId) return;
    const confirmed = window.confirm(
      "Are you sure you want to delete this order?",
    );
    if (!confirmed) return;

    setDeletingId(order.id);
    setError("");
    try {
      await api.admin.orders.remove(order.id, getAdminToken());
      await onChanged();
    } catch (requestError) {
      if (requestError.status === 401 || requestError.status === 403) {
        onUnauthorized();
        return;
      }
      setError(requestError.message || "Could not delete the order");
    } finally {
      setDeletingId("");
    }
  };

  const runShipmentAction = async (order, action) => {
    if (workingId) return;
    setWorkingId(order.id);
    setError("");
    try {
      await action();
      await onChanged();
    } catch (requestError) {
      if (requestError.status === 401 || requestError.status === 403) {
        onUnauthorized();
        return;
      }
      setError(requestError.message || "Could not update the UPS shipment");
    } finally {
      setWorkingId("");
    }
  };

  const retryShipment = (order) =>
    runShipmentAction(order, () =>
      api.admin.orders.retryShipment(order.id, getAdminToken()),
    );

  const markDroppedOff = (order) =>
    runShipmentAction(order, () =>
      api.admin.orders.markDroppedOff(order.id, getAdminToken()),
    );

  const voidShipment = (order) => {
    const confirmed = window.confirm(
      "Void this UPS label? The order payment will not be refunded.",
    );
    if (!confirmed) return;
    return runShipmentAction(order, () =>
      api.admin.orders.voidShipment(order.id, getAdminToken()),
    );
  };

  const downloadLabel = (order, labelIndex) =>
    runShipmentAction(order, () =>
      api.admin.orders.downloadLabel(order.id, labelIndex, getAdminToken()),
    );

  return (
    <section className="admin-panel">
      <div className="admin-panel-heading">
        <div>
          <p className="admin-eyebrow">Stripe payments</p>
          <h2>Orders</h2>
        </div>
      </div>

      {error && (
        <p className="admin-form-error" role="alert">
          {error}
        </p>
      )}

      <div className="admin-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Order</th>
              <th>Customer</th>
              <th>Address</th>
              <th>Product</th>
              <th>Total</th>
              <th>Payment Status</th>
              <th>UPS</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr key={order.id}>
                <td>
                  <small>{order.id}</small>
                </td>
                <td>
                  {order.customer.fullName}
                  <small>
                    <a href={`mailto:${order.customer.email}`}>
                      {order.customer.email}
                    </a>
                  </small>
                  <small>{order.customer.phone}</small>
                </td>
                <td>
                  <BillingAddressCell address={order.billingAddress} />
                </td>
                <td>
                  {order.product.name}
                  <small>Quantity: {order.quantity}</small>
                </td>
                <td>{formatAmount(order.totalAmount, order.currency)}</td>
                <td>
                  <span
                    className={`admin-payment-status is-${order.paymentStatus}`}
                  >
                    {formatPaymentStatus(order.paymentStatus)}
                  </span>
                </td>
                <td>
                  <ShipmentCell
                    order={order}
                    actions={{ retry: "Retry label" }}
                    workingId={workingId}
                    onRetry={retryShipment}
                    onDownload={downloadLabel}
                    onDroppedOff={markDroppedOff}
                    onVoid={voidShipment}
                  />
                </td>
                <td>{formatDate(order.createdAt)}</td>
                <td>
                  <button
                    type="button"
                    className="admin-delete"
                    onClick={() => removeOrder(order)}
                    disabled={Boolean(deletingId)}
                  >
                    {deletingId === order.id ? "Deleting..." : "Delete"}
                  </button>
                </td>
              </tr>
            ))}
            {orders.length === 0 && (
              <tr>
                <td colSpan="9">No orders yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
